/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import axios from 'axios';

import { LoginActions } from 'sarsaparilla';
import { getAuthHeader, isGuestLoggedIn } from 'sarsaparilla/auth';

import { useTypedDispatch } from '../../redux';
import type { ReduxStore } from './Navbar.types';

export const MOBILE_MENU_TRIGGER_ID = 'mobile-menu-toggle-btn';

export const GUEST_LOGIN_ALLOWED_PATH =
    /^\/(sitepass|cart|account\/session|timed-entry|ticket|activitypass|gateways)/;

export function useLogoutGuestOnDisallowedRoute(isGuestAllowed?: boolean) {
    const dispatch = useTypedDispatch();
    const { pathname } = useLocation();
    const isGuest = useSelector((state: ReduxStore) => state.auth?.isGuest ?? false);

    React.useEffect(() => {
        if (!isGuest) return;

        const isAllowed = isGuestAllowed && GUEST_LOGIN_ALLOWED_PATH.test(pathname);

        if (!isAllowed) {
            dispatch(LoginActions.logout());
        }
    }, [isGuest, isGuestAllowed, pathname, dispatch]);
}

// ---------------------------------------------------------------------------------------
export function useIsStickyStuck(el: HTMLElement | null) {
    const [isPinned, setPinned] = React.useState(false);

    React.useEffect(() => {
        // If IntersectionObserver is not supported, return early
        if (typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            ([e]) => {
                setPinned(e.intersectionRatio < 1);
            },
            { threshold: [1] }
        );

        if (el) {
            observer.observe(el);
        }

        return () => {
            observer.disconnect();
        };
    }, [el]);

    return isPinned;
}

// ---------------------------------------------------------------------------------------
export function useUserName() {
    const account = useSelector((state: ReduxStore) => state.auth?.account?.account);

    if (isGuestLoggedIn()) {
        return 'Guest';
    }

    const rawFirstName = account?.first_name;
    if (!rawFirstName) return undefined;

    const capitalizedFirstName =
        rawFirstName.charAt(0).toUpperCase() + rawFirstName.slice(1);
    const lastInitial = account?.last_name?.[0]
        ? `${account.last_name[0].toUpperCase()}.`
        : '';
    return `${capitalizedFirstName.trim()} ${lastInitial}`.trim();
}

// ---------------------------------------------------------------------------------------
type CachedAvatar = {
    url: string;
    pictureId: string;
};

/**
 * useGetAvatarUrl returns the avatar URL for the current user and re-fetches it if the
 * account's profile_picture_id changes.
 * The result is cached in session storage
 */
export function useGetAvatarUrl() {
    const [avatarUrl, setAvatarUrl] = React.useState<string | undefined>(() => {
        const cachedAvatar = getCachedAvatar();
        if (!cachedAvatar) return undefined;
        if (isAvatarUrlExpired(cachedAvatar.url)) {
            removeCachedAvatar();
            return undefined;
        }
        return cachedAvatar.url;
    });

    const pictureId = useSelector(
        (state: ReduxStore) => state.auth?.account?.account?.profile_picture_id
    );

    // Fetch avatar URL
    React.useEffect(() => {
        // If the user is a guest or if no picture ID (not logged in), clear avatar URL
        if (isGuestLoggedIn() || !pictureId) {
            setAvatarUrl(undefined);
            removeCachedAvatar();
            return;
        }

        const cachedAvatar = getCachedAvatar();

        if (
            cachedAvatar?.pictureId === pictureId &&
            !isAvatarUrlExpired(cachedAvatar.url)
        ) {
            // Use cached avatar
            setAvatarUrl(cachedAvatar.url);
            return;
        }

        // Otherwise we fetch the avatar URL from the media service
        const controller = new AbortController();

        axios
            .get(`/api/media/external/${pictureId}`, {
                headers: { Authorization: getAuthHeader() },
                signal: controller.signal,
            })
            .then(({ data }) => {
                setAvatarUrl(data.url);
                setCachedAvatar({ url: data.url, pictureId });
            })
            .catch(() => {
                // Eat error but clear local storage
                removeCachedAvatar();
                setAvatarUrl(undefined);
            });

        return () => {
            controller.abort();
        };
    }, [pictureId]);

    return avatarUrl;
}

// ---------------------------------------------------------------------------------------

function getCachedAvatar() {
    try {
        const unparsedAvatar = window.sessionStorage.getItem('avatar');
        if (unparsedAvatar) {
            const avatar: CachedAvatar = JSON.parse(unparsedAvatar);
            return avatar;
        }
    } catch (error) {
        return null;
    }

    return null;
}

function setCachedAvatar({ url, pictureId }: CachedAvatar) {
    try {
        window.sessionStorage.setItem('avatar', JSON.stringify({ url, pictureId }));
    } catch (error) {
        // Do nothing
    }
}

function removeCachedAvatar() {
    try {
        window.sessionStorage.removeItem('avatar');
    } catch (error) {
        // Do nothing
    }
}

function parseExpiresMsFromUrl(url: string): number | null {
    try {
        const expiresStr = new URL(url).searchParams.get('Expires');
        if (!expiresStr) return null;
        const seconds = Number.parseInt(expiresStr, 10);
        return Number.isFinite(seconds) ? seconds * 1000 : null;
    } catch {
        return null;
    }
}

function isAvatarUrlExpired(url: string): boolean {
    // URL expires after 3 minutes
    // Refetch slightly before the signed URL's Expires timestamp to negate any time difference
    const EXPIRATION_BUFFER_MS = 20000;
    const expiresMs = parseExpiresMsFromUrl(url);
    if (expiresMs === null) return false;
    // True when less than EXPIRATION_BUFFER_MS of life remains on the signed URL.
    // e.g., expiresMs = 12:00:00 and now = 11:59:50 => (now + 20s) >= expiresMs => refetch.
    return Date.now() + EXPIRATION_BUFFER_MS >= expiresMs;
}

// cSpell:ignore sitepass, activitypass
